// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Fragment, useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { apiBaseUrl } from '../api/client'
import { SEVERITY_CONFIG, useAssetInspections, useLinkEvidence, useUnlinkEvidence, usePatchEvidence, type ComplianceRule, type ComplianceControl, type ComplianceEvidenceItem, type EvidenceValidityInput } from '../hooks/useComplianceApi'
import { evidenceDocumentHref } from '../lib/documentPageHighlight'
import { usePropertyDefinitions, useAssetProperties } from '../hooks/useQueries'
import { PropertySourceBadge } from './PropertySourceBadge'
import { useDocumentUrlResolver } from '../hooks/useDocumentUrlResolver'
import { AppIcon, type AppIconSize } from './ui/icon'
import { mdiAutorenew, mdiRefresh, mdiCheckCircle, mdiAlertCircle, mdiAlertOctagon, mdiChevronDown, mdiChevronUp, mdiChevronRight, mdiOpenInNew, mdiShieldCheck, mdiPaperclip, mdiClose, mdiAlertOutline, mdiClockAlert, mdiHelpCircleOutline, mdiMinusCircleOutline, mdiPencil, mdiLaunch, mdiHomeOutline, mdiEye, mdiEyeOff } from '@mdi/js'
import { AssetMilestoneTimeline } from './AssetMilestoneTimeline'
import { EmptyState } from './ui/empty-state'
import { InspectionList } from './compliance/InspectionList'
import { conditionPropertyCodes, enumLabels, labelConditionValue, severityLabelKey, type EnumLabelsByCode } from './compliance/conditionLabels'
import { SECTION_ICONS } from '../lib/section-icons'

// The six-way evaluation status spectrum. See spec 2026-07-05-01 (UNKNOWN /
// NOT_APPLICABLE) and 2026-07-05-02 (EXPIRING, evidence validity).
export type EvaluationStatus = 'COMPLIANT' | 'EXPIRING' | 'NON_COMPLIANT' | 'UNKNOWN' | 'ERROR' | 'NOT_APPLICABLE'

interface ComplianceEvaluation {
    uid: string
    compliance_rule_uid: string
    asset_uid: string
    /** Set by the API so a rolled-up site list can say which asset each verdict is about. */
    asset_name?: string
    status: EvaluationStatus
    details?: Record<string, unknown>
    evidence_description?: string
    evidence?: ComplianceEvidenceItem[]
    issue_uid?: string
    evaluated_at: string
    next_evaluation_at?: string
    compliance_rule?: ComplianceRule & {
        compliance_control?: ComplianceControl
    }
}

interface DocumentListItem {
    uid: string
    name: string
    document_type_uid?: string
    expires_at?: string
    thumbnailUrl?: string
}

interface PaginatedResponse<T> {
    data: T[]
    total: number
    page: number
    limit: number
}

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token')
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: {
            ...getAuthHeaders(),
            ...options?.headers,
        },
    })
    if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw { status: res.status, ...body }
    }
    if (res.status === 204) return undefined as T
    return res.json()
}

/**
 * Shared status-pill config — the single source of truth for tone + icon +
 * label across rows, chips, and counters. Spec 2026-07-05-03 §2. Colors are
 * theme tokens only (never hardcoded hex/Tailwind palette classes) so light
 * and dark mode both stay correct. Ordering below is also the display order
 * used when sorting rows within a domain group (§4): actionable statuses
 * first (doubt surfaces), reassurance last.
 */
export const STATUS_PILL_CONFIG: Record<EvaluationStatus, {
    icon: string
    color: string
    bg: string
    /** Includes border-dashed for UNKNOWN (spec §2: "muted, dashed border"). */
    border: string
    labelKey: string
    /** Lower = more actionable / surfaced first within a domain group. */
    order: number
}> = {
    NON_COMPLIANT: {
        icon: mdiAlertCircle,
        color: 'text-destructive',
        bg: 'bg-destructive/10',
        border: 'border-destructive/25 border-l-[3px] border-l-destructive',
        labelKey: 'asset.status_non_compliant',
        order: 0,
    },
    EXPIRING: {
        icon: mdiClockAlert,
        color: 'text-warning',
        bg: 'bg-warning/10',
        border: 'border-warning/25 border-l-[3px] border-l-warning',
        labelKey: 'asset.status_expiring',
        order: 1,
    },
    ERROR: {
        icon: mdiAlertOctagon,
        color: 'text-warning',
        bg: 'bg-warning/10',
        border: 'border-warning/25 border-l-[3px] border-l-warning',
        labelKey: 'asset.status_error',
        order: 2,
    },
    UNKNOWN: {
        icon: mdiHelpCircleOutline,
        color: 'text-muted-foreground',
        bg: 'bg-muted/50',
        border: 'border-muted-foreground/25 border-dashed border-l-[3px] border-l-muted-foreground',
        labelKey: 'asset.status_unknown',
        order: 3,
    },
    COMPLIANT: {
        icon: mdiCheckCircle,
        color: 'text-success',
        bg: 'bg-success/10',
        border: 'border-success/25 border-l-[3px] border-l-success',
        labelKey: 'asset.status_compliant',
        order: 4,
    },
    NOT_APPLICABLE: {
        icon: mdiMinusCircleOutline,
        color: 'text-muted-foreground/70',
        bg: 'bg-muted/30',
        border: 'border-muted-foreground/15 border-l-[3px] border-l-muted-foreground/40',
        labelKey: 'asset.status_not_applicable',
        order: 5,
    },
}

/** Statuses that should keep a domain group expanded by default (§4.2) and drive header-stat visibility. */
const ACTIONABLE_STATUSES: EvaluationStatus[] = ['NON_COMPLIANT', 'EXPIRING', 'ERROR', 'UNKNOWN']

export function StatusPill({ status, size = 'md', className = '' }: { status: EvaluationStatus; size?: AppIconSize; className?: string }) {
    const { t } = useTranslation('compliance')
    const config = STATUS_PILL_CONFIG[status]
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span data-testid={`evaluation-status-${status}`} className={`inline-flex ${className}`}>
                        <AppIcon path={config.icon} size={size} className={`${config.color} flex-shrink-0`} />
                    </span>
                </TooltipTrigger>
                <TooltipContent>{t(config.labelKey)}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

interface GroupedEvaluations {
    domain: string
    control: ComplianceControl
    evaluations: ComplianceEvaluation[]
}

/**
 * Comparison operators of the rule `conditions` expression language, rendered
 * as math symbols in the applicability column. `is_set`/`is_null` and the
 * `always` shortcut are handled separately (they read as sentences, not
 * comparisons). Mirrors the closed operator set of the backend evaluator
 * (internal/handlers/compliance/service.go).
 */
const CONDITION_OP_SYMBOLS: Record<string, string> = {
    eq: '=',
    neq: '≠',
    lt: '<',
    lte: '≤',
    gt: '>',
    gte: '≥',
    in: '∈',
}

interface ConditionLeaf {
    property: string
    op: string
    value?: unknown
}

/**
 * Flattens a rule `conditions` expression into its property leaves for the
 * applicability column. `{always: true}` / empty → [] (the rule always
 * applies). Combinators are flattened — each leaf renders as its own chip;
 * the all/any distinction is not surfaced (the seed only carries single-leaf
 * conditions today, and a chip list stays readable if that changes).
 */
function conditionLeaves(expr: unknown): ConditionLeaf[] {
    if (!expr || typeof expr !== 'object') return []
    const e = expr as Record<string, unknown>
    if (e.always) return []
    if (Array.isArray(e.all)) return e.all.flatMap(conditionLeaves)
    if (Array.isArray(e.any)) return e.any.flatMap(conditionLeaves)
    if (typeof e.property === 'string') return [e as unknown as ConditionLeaf]
    return []
}

// --- Derivation resolution for column 2 ---
// A rule's applicability may hinge on a *derived* property (e.g. the IGH rule
// keys off igh_classification, itself derived from height_meters). Rather than
// show the opaque derived condition ("Classement IGH ≠ none"), column 2 unwinds
// one level: it replays the derivation against the asset's actual property
// values to find which branch fired, and shows *that* physical comparison
// ("Hauteur (m) 70 ≥ 28") linking to the driving property. This mirrors the
// backend derivation evaluator (07-derivations processor) closely enough for an
// explanation — it is display-only, never a source of truth.

interface DerivationRule { output?: unknown; priority?: number; conditions?: unknown }
interface DerivationInfo { uid?: string; rules: DerivationRule[] }

function toNum(v: unknown): number {
    if (typeof v === 'number') return v
    if (typeof v === 'string' && v.trim() !== '') return Number(v)
    return NaN
}

function evalDerivLeaf(leaf: ConditionLeaf, props: Record<string, unknown>): boolean {
    const v = props[leaf.property]
    const cv = leaf.value
    switch (leaf.op) {
        case 'gte': return toNum(v) >= toNum(cv)
        case 'gt': return toNum(v) > toNum(cv)
        case 'lte': return toNum(v) <= toNum(cv)
        case 'lt': return toNum(v) < toNum(cv)
        case 'eq': return v != null && String(v) === String(cv)
        case 'neq': return String(v) !== String(cv)
        case 'in': return Array.isArray(cv) && cv.map(String).includes(String(v))
        case 'is_set': case 'exists': case 'not_null': return v != null && v !== ''
        case 'not_set': case 'not_exists': return v == null || v === ''
        default: return false
    }
}

function evalDerivExpr(expr: unknown, props: Record<string, unknown>): boolean {
    if (expr == null) return true
    if (typeof expr !== 'object') return false
    const e = expr as Record<string, unknown>
    if (Object.keys(e).length === 0) return true // {} = catch-all default branch
    if (e.always) return true
    if (Array.isArray(e.all)) return e.all.every(x => evalDerivExpr(x, props))
    if (Array.isArray(e.any)) return e.any.some(x => evalDerivExpr(x, props))
    if (typeof e.property === 'string') return evalDerivLeaf(e as unknown as ConditionLeaf, props)
    return false
}

/**
 * Replays a derivation against the asset's actual property values and returns
 * the source-property leaves of the branch that fired (first match by ascending
 * priority) — or null when the matching branch has no explanatory leaves (e.g.
 * the catch-all default), in which case column 2 falls back to the derived
 * condition itself.
 */
function resolveDerivationLeaves(deriv: DerivationInfo, props: Record<string, unknown>): ConditionLeaf[] | null {
    const rules = [...deriv.rules].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    for (const r of rules) {
        if (evalDerivExpr(r.conditions, props)) {
            const leaves = conditionLeaves(r.conditions)
            return leaves.length > 0 ? leaves : null
        }
    }
    return null
}

function fmtValue(raw: unknown, t: (k: string) => string, labels?: Record<string, string>): string {
    const v = labelConditionValue(raw, labels)
    if (v === true) return t('asset.value_yes')
    if (v === false) return t('asset.value_no')
    if (v == null || v === '') return '—'
    if (Array.isArray(v)) return v.join(', ')
    return String(v)
}

/**
 * The requirement half of a condition leaf, without the asset's value — the
 * threshold side: "Hauteur (m) ≥ 28", "Catégorie SSI renseignée",
 * "Établissement ERP = Oui", "Année de construction < 1997".
 */
function requirementText(leaf: ConditionLeaf, propNameByCode: Record<string, string>, t: (k: string) => string, labels?: Record<string, string>): string {
    const name = propNameByCode[leaf.property] || leaf.property
    if (leaf.op === 'is_set' || leaf.op === 'exists' || leaf.op === 'not_null') {
        return `${name} ${t('asset.cond_set')}`
    }
    if (leaf.op === 'is_null' || leaf.op === 'not_set' || leaf.op === 'not_exists') {
        return `${name} ${t('asset.cond_not_set')}`
    }
    if (leaf.op === 'eq') {
        return `${name} = ${fmtValue(leaf.value, t, labels)}`
    }
    const sym = CONDITION_OP_SYMBOLS[leaf.op] || leaf.op
    return `${name} ${sym} ${fmtValue(leaf.value, t, labels)}`
}

/**
 * A resolved applicability condition, split into its two independently-editable
 * sides so the table can link them separately (spec: two links). `requirement`
 * is the threshold — for a derived condition it carries `requirementDerivationUid`
 * and links to the derivation where the threshold lives (e.g. the 28 m). `value`
 * is the asset's actual value, linked to the Data tab where it's edited (the
 * 70 m). `propCode` identifies the property both sides refer to.
 */
/** Document provenance of an asset property value, for the value column's source badge. */
interface ValueSource {
    propertyUid?: string
    sourceCode?: string
    details: Record<string, any>
}

interface ResolvedCondition {
    propCode: string
    requirement: string
    requirementDerivationUid?: string
    value: string
}

/**
 * Turns a rule's applicability conditions into resolved conditions. A leaf on a
 * derived property is unwound to the physical comparison from the derivation
 * branch that fired (e.g. igh_classification → "Hauteur ≥ 28"), carrying that
 * derivation's uid so its threshold side links to the derivation editor.
 */
function resolveApplicability(conditions: unknown, props: Record<string, unknown>, propNameByCode: Record<string, string>, derivationByTarget: Record<string, DerivationInfo>, t: (k: string) => string, enumLabelsByCode: EnumLabelsByCode = {}): ResolvedCondition[] {
    const out: ResolvedCondition[] = []
    for (const leaf of conditionLeaves(conditions)) {
        const deriv = derivationByTarget[leaf.property]
        const derivedLeaves = deriv ? resolveDerivationLeaves(deriv, props) : null
        if (derivedLeaves && derivedLeaves.length > 0) {
            for (const src of derivedLeaves) {
                out.push({ propCode: src.property, requirement: requirementText(src, propNameByCode, t, enumLabelsByCode[src.property]), requirementDerivationUid: deriv.uid, value: fmtValue(props[src.property], t, enumLabelsByCode[src.property]) })
            }
        } else {
            out.push({ propCode: leaf.property, requirement: requirementText(leaf, propNameByCode, t, enumLabelsByCode[leaf.property]), value: fmtValue(props[leaf.property], t, enumLabelsByCode[leaf.property]) })
        }
    }
    return out
}

/** A small chip, plain when it has no href, an external link (with ↗) when it does. */
function LinkChip({ text, href, title, testid }: { text: string; href?: string; title?: string; testid: string }) {
    const className = 'inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground'
    if (!href) {
        return <span className={className} data-testid={testid}>{text}</span>
    }
    return (
        <a href={href} onClick={(e) => e.stopPropagation()} className={className} title={title} data-testid={testid}>
            {text}
            <AppIcon path={mdiLaunch} size="xs" className="flex-shrink-0 opacity-60" />
        </a>
    )
}

/**
 * Column "Applicable si": the requirement/threshold side of each condition. For
 * a derived condition the chip links to the derivation's Rules tab, where the
 * threshold (e.g. 28 m) is edited.
 */
function RequirementCell({ conditions, org }: { conditions: ResolvedCondition[]; org: string }) {
    const { t } = useTranslation('compliance')
    if (conditions.length === 0) {
        return <span className="text-xs text-muted-foreground/60 italic">{t('asset.always_applicable')}</span>
    }
    return (
        <div className="flex flex-wrap gap-1">
            {conditions.map((c, i) => (
                <LinkChip
                    key={i}
                    text={c.requirement}
                    href={c.requirementDerivationUid ? `/${org}/derivations/${c.requirementDerivationUid}?tab=rules` : undefined}
                    title={t('asset.condition_threshold_link_hint')}
                    testid={`applicability-requirement-${c.propCode}`}
                />
            ))}
        </div>
    )
}

/**
 * Column "Valeur": the asset's actual value for each condition property, linked
 * to the Data tab where the value (e.g. 70 m) is edited.
 */
function ValueCell({ conditions, org, assetUid, sourceByCode }: { conditions: ResolvedCondition[]; org: string; assetUid?: string; sourceByCode: Record<string, ValueSource> }) {
    const { t } = useTranslation('compliance')
    if (conditions.length === 0) {
        return <span className="text-xs text-muted-foreground/40">—</span>
    }
    return (
        <div className="flex flex-wrap gap-1">
            {conditions.map((c, i) => {
                const source = sourceByCode[c.propCode]
                return (
                    <span key={i} className="inline-flex items-center gap-1">
                        <LinkChip
                            text={c.value}
                            href={assetUid ? `/${org}/assets/${assetUid}?tab=data` : undefined}
                            title={t('asset.condition_link_hint')}
                            testid={`applicability-value-${c.propCode}`}
                        />
                        {source && assetUid && (
                            <span data-testid={`applicability-source-${c.propCode}`} onClick={(e) => e.stopPropagation()}>
                                <PropertySourceBadge
                                    org={org}
                                    size="sm"
                                    sourceCode={source.sourceCode}
                                    sourceDetails={source.details}
                                    origin={{ kind: 'asset', assetUid, propertyUid: source.propertyUid }}
                                />
                            </span>
                        )}
                    </span>
                )
            })}
        </div>
    )
}

/**
 * Resolves a rule's free-text source reference (e.g. "NF S 61-933",
 * "Décret n° 72-1120", "CCH art. R.146-1") to a URL where the actual text can
 * be consulted. French legal texts (codes, décrets, arrêtés, lois) resolve to
 * a Légifrance search; standards (NF / EN / ISO) to the AFNOR catalogue (the
 * full text is paywalled there, but it's the canonical landing page). This is
 * a heuristic on the reference string — no per-rule URL is stored — so it
 * degrades gracefully to a search rather than promising a deep link.
 */
function sourceRefUrl(sourceRef: string): string {
    const ref = sourceRef.trim()
    const q = encodeURIComponent(ref)
    if (/^(NF|EN|ISO|CEN|CEI|IEC)\b/i.test(ref)) {
        return `https://www.boutique.afnor.org/fr-fr/recherche/resultat/?text=${q}`
    }
    return `https://www.legifrance.gouv.fr/search/all?query=${q}`
}

/**
 * Renders a rule's source reference as an external link to the underlying
 * regulatory text. Prefers the rule's exact `source_url` (a Légifrance
 * article/text page, an AFNOR standard page, …) when present; otherwise falls
 * back to a heuristic search built from the reference string. Opens in a new
 * tab; stops row-expand propagation so clicking never also toggles the detail
 * row.
 */
function SourceRefLink({ sourceRef, sourceUrl }: { sourceRef: string; sourceUrl?: string }) {
    const { t } = useTranslation('compliance')
    return (
        <a
            href={sourceUrl || sourceRefUrl(sourceRef)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground hover:underline"
            title={t('asset.source_ref_link_hint', { ref: sourceRef })}
            data-testid="compliance-source-ref-link"
        >
            {sourceRef}
            <AppIcon path={mdiLaunch} size="xs" className="flex-shrink-0 opacity-70" />
        </a>
    )
}

export function AssetCompliance({ org, assetUid, showTimeline, rollupDescendants }: { org: string; assetUid?: string; showTimeline?: boolean; rollupDescendants?: boolean }) {
    const { t, i18n } = useTranslation('compliance')
    const lang = i18n.language?.split('-')[0] || 'en'
    const queryClient = useQueryClient()
    const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set())

    // Show-all toggle, mirroring the Data tab's (AssetProperties.tsx): one
    // click reveals the whole regulatory file — every domain expanded and the
    // NOT_APPLICABLE rules included — instead of the reduced view that only
    // carries a signal. State lives in the same `?show=all` search param as
    // the Data tab, so it deep-links and one preference holds across tabs.
    const navigate = useNavigate({ from: '/$org/assets/$assetUid' })
    const search = useSearch({ strict: false }) as { show?: string }
    const showAll = search.show === 'all'

    // Compliance is re-evaluated automatically server-side whenever the asset's
    // data changes: the post_data_update job runs normalization → derivation →
    // compliance on every property write (job_post_data_update.go). So the tab
    // has no manual "run check" action — it just keeps itself fresh by polling
    // and refetching on window focus, surfacing the auto-computed result.
    const { data: evaluations, isLoading, error } = useQuery({
        queryKey: ['compliance-evaluations', org, assetUid, rollupDescendants],
        queryFn: () => apiFetch<PaginatedResponse<ComplianceEvaluation>>(
            // A site (building_group) rolls up its descendant parcels' and
            // buildings' evaluations: the natural-risk / ERP obligations live on
            // the parcel and the technical ones on each building, so without the
            // rollup a copropriété's site reads as an almost empty regulatory file.
            `/api/v1/organizations/${org}/compliance/evaluations?asset_uid=${assetUid}${rollupDescendants ? '&include_descendants=true' : ''}&rule_class=regulatory&limit=100`
        ),
        enabled: !!org && !!assetUid,
        select: (data) => data.data,
        refetchInterval: 15000,
        refetchOnWindowFocus: true,
        staleTime: 0,
    })

    // Manual "force a re-check now" escape hatch. Data changes already
    // re-evaluate server-side (see the query comment above), but a rule edit
    // or a just-fixed data issue can be re-checked on demand without waiting
    // for the next poll — the button re-runs the evaluation and refetches.
    const forceCheck = useMutation({
        mutationFn: () => apiFetch<{ status: string }>(
            `/api/v1/organizations/${org}/assets/${assetUid}/compliance/evaluate`,
            { method: 'POST', body: JSON.stringify({ schedule_children_evaluation: true }) },
        ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['compliance-evaluations', org, assetUid] })
            queryClient.invalidateQueries({ queryKey: ['compliance-inspections', org] })
        },
    })

    // Localized names for the property codes referenced by rule conditions
    // (applicability column). One fetch for the whole table.
    const { data: propertyDefs } = usePropertyDefinitions(org, { entityType: 'asset', enabled: !!org && !!assetUid })
    const propNameByCode = useMemo(() => {
        const map: Record<string, string> = {}
        for (const def of propertyDefs || []) {
            if (!def?.code) continue
            const name = typeof def.name === 'string' ? def.name : (def.name?.[lang] || def.name?.en)
            if (name) map[def.code] = name
        }
        return map
    }, [propertyDefs, lang])

    // Document source (page, boxes) of each condition property's value, for the
    // value column's source badge. Same rows as the Data tab (one shared query).
    const { data: assetValues } = useAssetProperties(org, assetUid || '')
    const valueSourceByCode = useMemo(() => {
        const codeByUid: Record<string, string> = {}
        for (const def of propertyDefs || []) if (def?.uid && def.code) codeByUid[def.uid] = def.code
        const map: Record<string, ValueSource> = {}
        for (const v of assetValues || []) {
            const code = v.property_uid ? codeByUid[v.property_uid] : undefined
            const details = (v as any).source_details as Record<string, any> | null | undefined
            if (code && details?.document_uid) map[code] = { propertyUid: v.property_uid || undefined, sourceCode: (v as any).source_code, details }
        }
        return map
    }, [assetValues, propertyDefs])

    // Derivation resolution for column 2: any rule condition on a *derived*
    // property (e.g. igh_classification) is unwound to its physical driver. We
    // fetch the org's derivations once, keep only those that back a condition
    // property actually referenced here, and load their branch rules.
    // Is there a rulebook for this asset's country at all? Only consulted to
    // word the empty state, so it is cheap and never blocks the list.
    const { data: scope } = useQuery({
        queryKey: ['compliance-scope', org, assetUid],
        queryFn: () => apiFetch<{ jurisdiction: string, applicable_rules_count: number }>(
            `/api/v1/organizations/${org}/assets/${assetUid}/compliance/scope`
        ),
        enabled: !!org && !!assetUid,
    })

    const { data: derivationList } = useQuery({
        queryKey: ['compliance-derivations', org],
        queryFn: () => apiFetch<{ data: Array<{ uid: string; target_property?: { code?: string } }> }>(
            `/api/v1/organizations/${org}/derivations?limit=200`
        ),
        enabled: !!org && !!assetUid,
        select: (d) => d.data || [],
    })
    const derivationUidByTarget = useMemo(() => {
        const map: Record<string, string> = {}
        for (const d of derivationList || []) {
            const code = d.target_property?.code
            if (code) map[code] = d.uid
        }
        return map
    }, [derivationList])
    const neededDerivTargets = useMemo(() => {
        const codes = new Set<string>()
        for (const ev of evaluations || []) {
            for (const leaf of conditionLeaves(ev.compliance_rule?.conditions)) {
                if (derivationUidByTarget[leaf.property]) codes.add(leaf.property)
            }
        }
        return [...codes].sort()
    }, [evaluations, derivationUidByTarget])
    const { data: derivationByTarget } = useQuery({
        queryKey: ['compliance-derivation-details', org, neededDerivTargets.join(',')],
        queryFn: async () => {
            const entries = await Promise.all(neededDerivTargets.map(async (code) => {
                const detail = await apiFetch<{ uid?: string; decision_table?: DerivationRule[] }>(
                    `/api/v1/organizations/${org}/derivations/${derivationUidByTarget[code]}`
                )
                return [code, { uid: detail.uid || derivationUidByTarget[code], rules: detail.decision_table || [] }] as const
            }))
            return Object.fromEntries(entries) as Record<string, DerivationInfo>
        },
        enabled: !!org && neededDerivTargets.length > 0,
    })

    // Enum labels of every property a condition reads (directly or through the
    // derivation it unwinds to), so the applicability column shows
    // "Destination réglementaire ≠ Technique", never the stored code.
    const conditionEnumProps = useMemo(() => {
        const codes = new Set<string>()
        for (const ev of evaluations || []) conditionPropertyCodes(ev.compliance_rule?.conditions, codes)
        for (const deriv of Object.values(derivationByTarget || {})) {
            for (const r of deriv.rules) conditionPropertyCodes(r.conditions, codes)
        }
        return (propertyDefs || [])
            .filter((def) => def?.code && def.uid && codes.has(def.code) && (def as { data_type?: string }).data_type === 'enum')
            .map((def) => ({ code: def.code as string, uid: def.uid as string }))
            .sort((a, b) => a.code.localeCompare(b.code))
    }, [evaluations, derivationByTarget, propertyDefs])
    const { data: enumValuesByCode } = useQuery({
        queryKey: ['compliance-condition-enums', org, conditionEnumProps.map((p) => p.uid).join(',')],
        queryFn: async () => {
            const entries = await Promise.all(conditionEnumProps.map(async (p) => {
                const res = await apiFetch<{ data?: Array<{ value?: string; name?: Record<string, string> }> | null }>(
                    `/api/v1/organizations/${org}/properties/${p.uid}/enum-values`
                )
                return [p.code, res.data || []] as const
            }))
            return Object.fromEntries(entries)
        },
        enabled: !!org && conditionEnumProps.length > 0,
    })
    const enumLabelsByCode = useMemo(() => {
        const out: EnumLabelsByCode = {}
        for (const [code, values] of Object.entries(enumValuesByCode || {})) out[code] = enumLabels(values, lang)
        return out
    }, [enumValuesByCode, lang])

    const grouped = useMemo(() => {
        if (!evaluations?.length) return []
        const groups = new Map<string, GroupedEvaluations>()
        for (const ev of evaluations) {
            const control = ev.compliance_rule?.compliance_control
            const domain = control?.domain || 'UNCLASSIFIED'
            if (!groups.has(domain)) {
                groups.set(domain, {
                    domain,
                    control: control!,
                    evaluations: [],
                })
            }
            groups.get(domain)!.evaluations.push(ev)
        }
        // Ordering inside a group (spec §4.1): actionable statuses surface first
        // (NON_COMPLIANT -> EXPIRING -> ERROR -> UNKNOWN), reassurance last
        // (COMPLIANT), NOT_APPLICABLE trails everything (rendered separately,
        // under its own divider, but sorted here too so the split is trivial).
        // Ties broken by severity descending (1 = most severe), then rule label.
        for (const group of groups.values()) {
            group.evaluations.sort((a, b) => {
                const orderDiff = STATUS_PILL_CONFIG[a.status].order - STATUS_PILL_CONFIG[b.status].order
                if (orderDiff !== 0) return orderDiff
                const sevA = a.compliance_rule?.severity ?? 3
                const sevB = b.compliance_rule?.severity ?? 3
                if (sevA !== sevB) return sevA - sevB // 1 = highest severity, surfaces first
                const labelA = a.compliance_rule?.label?.[lang] || a.compliance_rule?.label?.en || a.compliance_rule?.code || ''
                const labelB = b.compliance_rule?.label?.[lang] || b.compliance_rule?.label?.en || b.compliance_rule?.code || ''
                return labelA.localeCompare(labelB)
            })
        }
        // Sort by domain label
        return Array.from(groups.values()).sort((a, b) =>
            (t(`domains.${a.domain}`, a.domain)).localeCompare(t(`domains.${b.domain}`, b.domain))
        )
    }, [evaluations, t, lang])

    // Domains worth opening on arrival — also the set we fall back to when the
    // show-all toggle is switched back off.
    const actionableDomains = useMemo(
        () => grouped.filter(g => g.evaluations.some(e => ACTIONABLE_STATUSES.includes(e.status))).map(g => g.domain),
        [grouped]
    )

    // Collapsed view (default): NOT_APPLICABLE rules are dropped entirely, and
    // a domain left with nothing to say disappears with them. Expanded view
    // (show=all): the full set, unchanged.
    const displayGroups = useMemo(() => {
        if (showAll) return grouped
        return grouped
            .map(g => ({ ...g, evaluations: g.evaluations.filter(e => e.status !== 'NOT_APPLICABLE') }))
            .filter(g => g.evaluations.length > 0)
    }, [grouped, showAll])

    const hiddenNotApplicableCount = useMemo(
        () => (evaluations || []).filter(e => e.status === 'NOT_APPLICABLE').length,
        [evaluations]
    )

    const setShowAll = useCallback((all: boolean) => {
        navigate({
            search: (prev: Record<string, unknown>) => {
                if (all) return { ...prev, show: 'all' }
                const { show: _unused, ...rest } = prev
                return rest
            },
            replace: true,
        })
        // Revealing everything also opens every domain; folding back restores
        // the default "only what needs attention is open" posture.
        setExpandedDomains(new Set(all ? grouped.map(g => g.domain) : actionableDomains))
    }, [navigate, grouped, actionableDomains])

    // Default expansion (spec §4.2): domains containing at least one actionable
    // status (NON_COMPLIANT/EXPIRING/ERROR/UNKNOWN) start expanded; all-green or
    // all-N/A domains start collapsed. Seeded once, the first time grouped data
    // becomes available — manual toggling afterwards always wins (toggleDomain
    // below never gets overridden by this effect re-running).
    const hasSeededExpansion = useRef(false)
    useEffect(() => {
        if (hasSeededExpansion.current || grouped.length === 0) return
        hasSeededExpansion.current = true
        // Landing on `?show=all` starts with every domain open.
        setExpandedDomains(new Set(showAll ? grouped.map(g => g.domain) : actionableDomains))
    }, [grouped, actionableDomains, showAll])

    const stats = useMemo(() => {
        // NOT_APPLICABLE is excluded from every count and from the total (spec §3)
        // — it's not a compliance signal, it's a rule that doesn't apply here.
        const counted = evaluations?.filter(e => e.status !== 'NOT_APPLICABLE') || []
        return {
            compliant: counted.filter(e => e.status === 'COMPLIANT').length,
            expiring: counted.filter(e => e.status === 'EXPIRING').length,
            nonCompliant: counted.filter(e => e.status === 'NON_COMPLIANT').length,
            unknown: counted.filter(e => e.status === 'UNKNOWN').length,
            error: counted.filter(e => e.status === 'ERROR').length,
            total: counted.length,
        }
    }, [evaluations])

    const toggleDomain = (domain: string) => {
        setExpandedDomains(prev => {
            const next = new Set(prev)
            if (next.has(domain)) next.delete(domain)
            else next.add(domain)
            return next
        })
    }

    if (!assetUid) return null

    if (isLoading) {
        return (
            <div className="bg-card rounded-lg border border-border p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-muted rounded w-1/3" />
                    <div className="h-20 bg-muted rounded" />
                    <div className="h-16 bg-muted rounded" />
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="bg-card rounded-lg border border-border p-6">
                <p className="text-destructive">{t('asset.error_load')}</p>
            </div>
        )
    }

    const hasEvaluations = evaluations && evaluations.length > 0
    // An empty list has two opposite meanings — nobody has run a check, or
    // there is no rulebook for this asset's country. They look identical and
    // demand different words: the first invites an action, the second must not,
    // because repeating it would change nothing.
    const noRulesInScope = !hasEvaluations && scope != null && scope.applicable_rules_count === 0
    const lastEvalDate = hasEvaluations
        ? new Date(Math.max(...evaluations.map(e => new Date(e.evaluated_at).getTime())))
        : null

    const headerCounters: Array<{ status: EvaluationStatus; count: number; testid: string }> = [
        { status: 'NON_COMPLIANT', count: stats.nonCompliant, testid: 'stat-non-compliant' },
        { status: 'EXPIRING', count: stats.expiring, testid: 'stat-expiring' },
        { status: 'ERROR', count: stats.error, testid: 'stat-error' },
        { status: 'UNKNOWN', count: stats.unknown, testid: 'stat-unknown' },
        { status: 'COMPLIANT', count: stats.compliant, testid: 'stat-compliant' },
    ]

    return (
        <div className="space-y-4">
            {/* Milestone Timeline (top of Compliance — direct-asset only, see spec 04 §3) */}
            {showTimeline && assetUid && (
                <AssetMilestoneTimeline org={org} assetUid={assetUid} />
            )}

            {/* Header: compact status counters, last-check, force-check. The
                shield + "Statut de conformité" title was redundant with the tab
                label, and reports now live outside this tab, so both are gone. */}
            <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    {hasEvaluations ? (
                        <button
                            type="button"
                            onClick={() => setShowAll(!showAll)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
                            data-testid="compliance-show-all-toggle"
                            aria-pressed={showAll}
                        >
                            <AppIcon tone="muted" path={showAll ? mdiEyeOff : mdiEye} size="sm" />
                            <span>{showAll ? t('asset.show_applicable') : t('asset.show_all')}</span>
                            {!showAll && hiddenNotApplicableCount > 0 && (
                                <span className="text-xs font-normal text-muted-foreground" data-testid="compliance-hidden-count">
                                    · {t('asset.not_applicable_hidden', { count: hiddenNotApplicableCount })}
                                </span>
                            )}
                        </button>
                    ) : <span />}
                    <div className="flex items-center gap-3 flex-wrap" data-testid="compliance-stat-grid">
                        {hasEvaluations && headerCounters.filter(c => c.count > 0).map(({ status, count, testid }) => {
                            const cfg = STATUS_PILL_CONFIG[status]
                            return (
                                <span
                                    key={status}
                                    className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}
                                    data-testid={testid}
                                >
                                    <AppIcon path={cfg.icon} size="xs" />
                                    {count} {t(cfg.labelKey).toLowerCase()}
                                </span>
                            )
                        })}
                        {lastEvalDate && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span
                                            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                                            data-testid="compliance-auto-refresh"
                                        >
                                            <AppIcon path={mdiAutorenew} size="xs" className="flex-shrink-0" />
                                            {t('asset.last_check')} {lastEvalDate.toLocaleDateString()} {lastEvalDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('asset.auto_refresh_hint')}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => forceCheck.mutate()}
                            disabled={forceCheck.isPending}
                            aria-label={t('asset.force_check')}
                            title={t('asset.force_check')}
                            data-testid="run-compliance-check"
                        >
                            <AppIcon path={mdiRefresh} size="sm" className={forceCheck.isPending ? 'motion-safe:animate-spin' : ''} />
                        </Button>
                    </div>
                </div>

                {!hasEvaluations && (
                    <div className="text-center py-6 text-muted-foreground" data-testid={noRulesInScope ? 'compliance-empty-no-rules' : 'compliance-empty-not-run'}>
                        <AppIcon path={mdiShieldCheck} size="xl" className="mx-auto mb-2 opacity-30" />
                        <p>{noRulesInScope ? t('asset.empty_no_rules_title') : t('asset.empty_title')}</p>
                        <p className="text-sm mt-1">
                            {noRulesInScope
                                ? t('asset.empty_no_rules_subtitle', { jurisdiction: scope?.jurisdiction })
                                : t('asset.empty_subtitle')}
                        </p>
                    </div>
                )}
            </div>

            {/* Evaluations table — controls (rules) as rows, regulatory domains as
                section headers. Columns: control / applicability condition /
                status / evidence. */}
            {hasEvaluations && displayGroups.length === 0 && (
                <EmptyState
                    title={t('asset.all_not_applicable_hidden')}
                    data-testid="compliance-all-not-applicable"
                />
            )}
            {hasEvaluations && displayGroups.length > 0 && (
                <div className="bg-card rounded-lg border border-border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="compliance-table">
                            <thead>
                                <tr className="border-b border-border bg-muted/30 text-left">
                                    <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-[30%]">{t('asset.col_control')}</th>
                                    <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-[17%]">{t('asset.col_applicability')}</th>
                                    <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-[11%]">{t('asset.col_value')}</th>
                                    <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-[16%]">{t('asset.col_status')}</th>
                                    <th className="px-4 py-2 text-xs font-medium text-muted-foreground w-[26%]">{t('asset.col_evidence')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayGroups.map((group) => {
                                    const isExpanded = expandedDomains.has(group.domain)
                                    const groupNonCompliant = group.evaluations.filter(e => e.status === 'NON_COMPLIANT').length
                                    const groupExpiring = group.evaluations.filter(e => e.status === 'EXPIRING').length
                                    const groupUnknown = group.evaluations.filter(e => e.status === 'UNKNOWN').length
                                    const groupCompliant = group.evaluations.filter(e => e.status === 'COMPLIANT').length
                                    const groupTotal = group.evaluations.length
                                    // Not-applicable rows render last, muted, under their own
                                    // divider (spec §4.3) — the sort above already pushed them
                                    // to the end, so splitting is a simple partition.
                                    const mainRows = group.evaluations.filter(e => e.status !== 'NOT_APPLICABLE')
                                    const notApplicableRows = group.evaluations.filter(e => e.status === 'NOT_APPLICABLE')

                                    return (
                                        <Fragment key={group.domain}>
                                            {/* Domain section header (intertitre) */}
                                            <tr
                                                className="bg-muted/40 border-t border-border cursor-pointer hover:bg-muted/60 transition-colors"
                                                onClick={() => toggleDomain(group.domain)}
                                                data-testid={`compliance-domain-${group.domain}`}
                                            >
                                                <td colSpan={5} className="px-4 py-2">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <AppIcon
                                                                path={isExpanded ? mdiChevronDown : mdiChevronRight}
                                                                size="sm"
                                                                tone="muted"
                                                                className="flex-shrink-0"
                                                            />
                                                            <span className="font-medium text-foreground truncate">
                                                                {group.control?.name?.[lang] || group.control?.name?.en || t(`domains.${group.domain}`, group.domain)}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            {groupNonCompliant > 0 && (
                                                                <Badge variant="outline" className={`${STATUS_PILL_CONFIG.NON_COMPLIANT.bg} ${STATUS_PILL_CONFIG.NON_COMPLIANT.color} border-destructive/25`}>
                                                                    {t('asset.group_non_compliant_count', { count: groupNonCompliant })}
                                                                </Badge>
                                                            )}
                                                            {groupExpiring > 0 && (
                                                                <Badge variant="outline" className={`${STATUS_PILL_CONFIG.EXPIRING.bg} ${STATUS_PILL_CONFIG.EXPIRING.color} border-warning/25`}>
                                                                    {t('asset.group_expiring_count', { count: groupExpiring })}
                                                                </Badge>
                                                            )}
                                                            {groupUnknown > 0 && (
                                                                <Badge variant="outline" className={`${STATUS_PILL_CONFIG.UNKNOWN.bg} ${STATUS_PILL_CONFIG.UNKNOWN.color} border-muted-foreground/25`}>
                                                                    {t('asset.group_unknown_count', { count: groupUnknown })}
                                                                </Badge>
                                                            )}
                                                            {groupCompliant > 0 && (
                                                                <Badge variant="outline" className={`${STATUS_PILL_CONFIG.COMPLIANT.bg} ${STATUS_PILL_CONFIG.COMPLIANT.color} border-success/25`}>
                                                                    {groupCompliant} {t('asset.compliant_badge')}
                                                                </Badge>
                                                            )}
                                                            <span className="text-xs text-muted-foreground">{t('asset.rule_count', { count: groupTotal })}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && mainRows.map((ev) => (
                                                <EvaluationTableRow key={ev.uid} evaluation={ev} org={org} assetUid={assetUid} propNameByCode={propNameByCode} valueSourceByCode={valueSourceByCode} derivationByTarget={derivationByTarget || {}} enumLabelsByCode={enumLabelsByCode} />
                                            ))}
                                            {isExpanded && notApplicableRows.length > 0 && (
                                                <>
                                                    <tr>
                                                        <td
                                                            colSpan={5}
                                                            className="px-4 py-1.5 text-xs text-muted-foreground/70 bg-muted/20"
                                                            data-testid={`not-applicable-divider-${group.domain}`}
                                                        >
                                                            {t('asset.not_applicable_divider', { count: notApplicableRows.length })}
                                                        </td>
                                                    </tr>
                                                    {notApplicableRows.map((ev) => (
                                                        <EvaluationTableRow key={ev.uid} evaluation={ev} org={org} assetUid={assetUid} propNameByCode={propNameByCode} valueSourceByCode={valueSourceByCode} derivationByTarget={derivationByTarget || {}} enumLabelsByCode={enumLabelsByCode} muted />
                                                    ))}
                                                </>
                                            )}
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <PeriodicInspectionsSection org={org} assetUid={assetUid} />
        </div>
    )
}

/**
 * Periodic verification reports and visits (rules of class
 * `periodic_inspection`) carried by this asset itself — a building's
 * extinguisher or electrical check, a lift's VGP on the lift's own page. Kept
 * out of the regulatory file above and shown apart, so the two never mix; the
 * inspections of a building's equipment live on each equipment row of the
 * Equipment tab.
 */
function PeriodicInspectionsSection({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('compliance')
    const { data: items = [] } = useAssetInspections(org, assetUid, false)
    // An empty box under the rules only pointed at the Equipment tab, where
    // each equipment's checks already are (spec 2026-10-25-20): shown only
    // when the asset carries inspections of its own.
    if (items.length === 0) return null
    return (
        <div className="bg-card rounded-lg border border-border p-4" data-testid="compliance-periodic-inspections">
            <div className="text-sm font-medium text-foreground">{t('inspections.section_title')}</div>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('inspections.section_hint')}</p>
            {items.length > 0 && (
                <div className="mt-3">
                    <InspectionList org={org} items={items} testid="compliance-periodic-inspections-list" />
                </div>
            )}
        </div>
    )
}

/**
 * One control (rule×asset evaluation) as a table row. Columns: control label
 * + severity, applicability condition, status, evidence. The chevron on the
 * label expands a full-width detail row (description, code, refs, dates,
 * engine details).
 */
function EvaluationTableRow({ evaluation, org, assetUid, propNameByCode, valueSourceByCode, derivationByTarget, enumLabelsByCode, muted }: { evaluation: ComplianceEvaluation; org: string; assetUid?: string; propNameByCode: Record<string, string>; valueSourceByCode: Record<string, ValueSource>; derivationByTarget: Record<string, DerivationInfo>; enumLabelsByCode: EnumLabelsByCode; muted?: boolean }) {
    const { t, i18n } = useTranslation('compliance')
    const [showDetails, setShowDetails] = useState(false)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [detachTarget, setDetachTarget] = useState<ComplianceEvidenceItem | null>(null)
    const [evidenceDialogTarget, setEvidenceDialogTarget] = useState<ComplianceEvidenceItem | null>(null)
    const rule = evaluation.compliance_rule
    const statusConfig = STATUS_PILL_CONFIG[evaluation.status]
    const severityConfig = SEVERITY_CONFIG[rule?.severity || 3]
    const lang = i18n.language?.split('-')[0] || 'en'

    const evidence = evaluation.evidence || []
    // A verdict that belongs to a descendant (a building or the parcel of the
    // site being read) rather than to the asset whose page this is.
    const rolledUp = !!evaluation.asset_name && !!assetUid && evaluation.asset_uid !== assetUid
    // Evidence binds to the (rule, asset) pair of the *verdict*, not of the
    // page: on a rolled-up site, attaching a proof to a building's obligation
    // must land on that building, or the link would silently prove nothing.
    const rowAssetUid = evaluation.asset_uid || assetUid || ''
    const linkEvidence = useLinkEvidence(org, rowAssetUid)
    const unlinkEvidence = useUnlinkEvidence(org, rowAssetUid)

    // Resolved once, shared by the "Applicable si" (requirement/threshold) and
    // "Valeur" (asset value) columns so each side links to its own editor.
    const evalProps = (evaluation.details?.evaluated_properties as Record<string, unknown>) || {}
    const applicability = resolveApplicability(rule?.conditions, evalProps, propNameByCode, derivationByTarget, t, enumLabelsByCode)

    return (
        <>
            <tr
                className={`border-t border-border align-top ${muted ? 'opacity-60' : ''}`}
                data-testid={`compliance-rule-row-${evaluation.compliance_rule_uid}`}
            >
                {/* Col 1 — control */}
                <td className="px-4 py-3">
                    <div className="flex items-start gap-1.5">
                        <button
                            type="button"
                            onClick={() => setShowDetails(!showDetails)}
                            className="mt-0.5 text-muted-foreground hover:text-foreground flex-shrink-0"
                            aria-label={showDetails ? t('asset.hide_details') : t('asset.show_details')}
                            title={showDetails ? t('asset.hide_details') : t('asset.show_details')}
                            data-testid={`compliance-details-toggle-${evaluation.uid}`}
                        >
                            <AppIcon tone="muted" path={showDetails ? mdiChevronUp : mdiChevronDown} size="sm" />
                        </button>
                        <div className="min-w-0">
                            <span className="font-medium text-sm text-foreground">
                                {rule?.label?.[lang] || rule?.label?.en || rule?.code || t('asset.unknown_rule')}
                            </span>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <Badge variant="outline" className={severityConfig?.color || ''}>
                                    {t(severityLabelKey(rule?.severity))}
                                </Badge>
                                {/* On a site, the same rule appears once per building —
                                    without the owner the rows are indistinguishable. */}
                                {rolledUp && (
                                    <a
                                        href={`/${org}/assets/${evaluation.asset_uid}`}
                                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
                                        data-testid={`compliance-row-asset-${evaluation.uid}`}
                                    >
                                        <AppIcon path={mdiHomeOutline} size="xs" />
                                        {evaluation.asset_name}
                                    </a>
                                )}
                                {rule?.source_ref && <SourceRefLink sourceRef={rule.source_ref} sourceUrl={rule.source_url} />}
                            </div>
                        </div>
                    </div>
                </td>

                {/* Col 2 — the requirement / threshold that makes the control
                    applicable (threshold side links to the derivation) */}
                <td className="px-4 py-3">
                    <RequirementCell conditions={applicability} org={org} />
                </td>

                {/* Col 3 — the asset's actual value (links to the Data tab) */}
                <td className="px-4 py-3">
                    <ValueCell conditions={applicability} org={org} assetUid={assetUid} sourceByCode={valueSourceByCode} />
                </td>

                {/* Col 4 — status */}
                <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                        <StatusPill status={evaluation.status} size="sm" />
                        <span className={`text-xs font-medium ${statusConfig.color}`}>{t(statusConfig.labelKey)}</span>
                        {evaluation.issue_uid && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <a
                                            href={`/${org}/issues/${evaluation.issue_uid}`}
                                            className="text-muted-foreground hover:text-foreground"
                                            data-testid={`compliance-issue-link-${evaluation.uid}`}
                                        >
                                            <AppIcon path={mdiOpenInNew} size="xs" />
                                        </a>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('asset.view_issue')}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                    {evaluation.next_evaluation_at && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                            {t('asset.next_check')} {new Date(evaluation.next_evaluation_at).toLocaleDateString()}
                        </div>
                    )}
                </td>

                {/* Col 4 — evidence (justificatif) */}
                <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5" data-testid={`compliance-evidence-${evaluation.uid}`}>
                        {evidence.length === 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs text-warning" data-testid={`evidence-missing-${evaluation.uid}`}>
                                <AppIcon path={mdiAlertOutline} size="xs" />
                                {t('asset.no_evidence')}
                            </span>
                        ) : (
                            evidence.map((ev) => {
                                // Tone: destructive when the effective validity has actually
                                // lapsed (ev.expired, backend-computed); amber when this row's
                                // evaluation is EXPIRING — reusing the backend-derived status
                                // rather than re-deriving the warn window client-side (spec §5:
                                // deriveStatus already ran server-side, don't duplicate its
                                // logic). Neutral otherwise (far-future or permanent proof).
                                //
                                // A candidate is a document the rule declares as satisfying but
                                // that carries no date to anchor a periodic obligation on — it
                                // proves nothing yet, so it reads as a dashed lead rather than a
                                // proof, and the validity slot says what is missing.
                                const chipTone = ev.candidate
                                    ? 'border-dashed border-border bg-transparent text-muted-foreground'
                                    : ev.expired
                                        ? 'border-destructive/40 bg-destructive/10 text-destructive'
                                        : evaluation.status === 'EXPIRING'
                                            ? 'border-warning/40 bg-warning/10 text-warning'
                                            : 'border-border bg-muted/50 text-foreground'
                                const validityLabel = ev.candidate
                                    ? t('asset.evidence_candidate_label')
                                    : ev.effective_valid_until
                                        ? t('asset.evidence_valid_until_label') + ' ' + new Date(ev.effective_valid_until).toLocaleDateString()
                                        : t('asset.evidence_permanent_label')
                                // Auto and candidate items have no compliance_evidence row behind
                                // them, so there is nothing to PATCH or unlink — the way to
                                // change them is to fix the document.
                                const derived = !!(ev.auto || ev.candidate)
                                const chip = (
                                    <span
                                        key={ev.uid}
                                        className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${chipTone}`}
                                        data-testid={`evidence-item-${ev.uid}`}
                                    >
                                        <AppIcon path={SECTION_ICONS.documents} size="xs" />
                                        <a
                                            href={evidenceDocumentHref(org, ev.document_uid, ev.source_details, assetUid)}
                                            className="hover:underline max-w-[14rem] truncate"
                                            data-testid={`evidence-link-${ev.uid}`}
                                        >
                                            {ev.document_name}
                                        </a>
                                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">{validityLabel}</span>
                                        {ev.auto && (
                                            <Badge
                                                variant="outline"
                                                className="border-border text-muted-foreground text-[10px] px-1 py-0"
                                                title={t('asset.evidence_auto_tooltip')}
                                            >
                                                {t('asset.evidence_auto')}
                                            </Badge>
                                        )}
                                        {ev.expired && (
                                            <Badge variant="outline" className="border-destructive/40 text-destructive text-[10px] px-1 py-0">
                                                {t('asset.evidence_expired')}
                                            </Badge>
                                        )}
                                        {assetUid && !derived && (
                                            <button
                                                type="button"
                                                onClick={() => setEvidenceDialogTarget(ev)}
                                                className="ml-0.5 text-muted-foreground hover:text-foreground"
                                                aria-label={t('asset.edit_evidence_tooltip')}
                                                title={t('asset.edit_evidence_tooltip')}
                                                data-testid={`evidence-edit-${ev.uid}`}
                                            >
                                                <AppIcon path={mdiPencil} size="xs" />
                                            </button>
                                        )}
                                        {assetUid && !derived && (
                                            <button
                                                type="button"
                                                onClick={() => setDetachTarget(ev)}
                                                className="ml-0.5 text-muted-foreground hover:text-destructive"
                                                aria-label={t('asset.detach_evidence')}
                                                title={t('asset.detach_evidence')}
                                                data-testid={`evidence-detach-${ev.uid}`}
                                            >
                                                <AppIcon path={mdiClose} size="xs" />
                                            </button>
                                        )}
                                    </span>
                                )
                                if (!ev.note) return chip
                                return (
                                    <TooltipProvider key={ev.uid}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>{chip}</TooltipTrigger>
                                            <TooltipContent>{ev.note}</TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )
                            })
                        )}
                        {assetUid && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPickerOpen(true)}
                                className="h-6 px-2 text-xs"
                                data-testid={`evidence-attach-${evaluation.uid}`}
                            >
                                <AppIcon path={mdiPaperclip} size="xs" className="mr-1" />
                                {t('asset.attach_evidence')}
                            </Button>
                        )}
                    </div>

                    {/* Dialogs live in this cell (they portal out of the table anyway) */}
                    {assetUid && pickerOpen && (
                        <EvidencePickerDialog
                            org={org}
                            assetUid={assetUid}
                            open={pickerOpen}
                            onOpenChange={setPickerOpen}
                            linkedDocumentUids={new Set(evidence.map((e) => e.document_uid))}
                            isLinking={linkEvidence.isPending}
                            onConfirm={(documentUid, validity) => {
                                linkEvidence.mutate(
                                    { ruleUid: evaluation.compliance_rule_uid, documentUid, ...validity },
                                    { onSuccess: () => setPickerOpen(false) }
                                )
                            }}
                        />
                    )}
                    {assetUid && evidenceDialogTarget && (
                        <EvidenceEditDialog
                            org={org}
                            assetUid={rowAssetUid}
                            ruleUid={evaluation.compliance_rule_uid}
                            evidence={evidenceDialogTarget}
                            open={!!evidenceDialogTarget}
                            onOpenChange={(o) => { if (!o) setEvidenceDialogTarget(null) }}
                            onSaved={() => setEvidenceDialogTarget(null)}
                        />
                    )}
                    <AlertDialog open={!!detachTarget} onOpenChange={(o) => { if (!o) setDetachTarget(null) }}>
                        <AlertDialogContent data-testid="evidence-detach-dialog">
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t('asset.detach_confirm_title')}</AlertDialogTitle>
                                <AlertDialogDescription>{t('asset.detach_confirm_description')}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="evidence-detach-cancel">{t('asset.detach_cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                    data-testid="evidence-detach-confirm"
                                    onClick={() => {
                                        if (detachTarget && assetUid) {
                                            unlinkEvidence.mutate(
                                                { ruleUid: evaluation.compliance_rule_uid, evidenceUid: detachTarget.uid },
                                                { onSuccess: () => setDetachTarget(null) }
                                            )
                                        }
                                    }}
                                >
                                    {t('asset.detach_confirm_action')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </td>
            </tr>

            {/* Expanded detail row */}
            {showDetails && (
                <tr className={muted ? 'opacity-60' : ''}>
                    <td colSpan={5} className="px-4 pb-3">
                        <div className={`ml-6 p-3 rounded-lg text-sm ${statusConfig.bg} border ${statusConfig.border}`} data-testid={`compliance-details-${evaluation.uid}`}>
                            {(rule?.description?.[lang] || rule?.description?.en) && (
                                <p className="text-sm text-muted-foreground mb-2">
                                    {rule.description[lang] || rule.description.en}
                                </p>
                            )}
                            <div className="flex items-center gap-4 mb-2 text-xs text-muted-foreground flex-wrap">
                                {rule?.code && <span className="font-mono">{rule.code}</span>}
                                {rule?.jurisdiction && <span>{t('asset.jurisdiction')} {rule.jurisdiction}</span>}
                                <span>{t('asset.checked')} {new Date(evaluation.evaluated_at).toLocaleDateString()}</span>
                            </div>
                            {evaluation.evidence_description && (
                                <div className="mb-2">
                                    <span className="font-medium text-foreground">{t('asset.evidence_label')} </span>
                                    <span className="text-muted-foreground">{evaluation.evidence_description}</span>
                                </div>
                            )}
                            {(evaluation.status === 'NON_COMPLIANT' || evaluation.status === 'EXPIRING') &&
                                (rule?.remediation?.steps?.length ?? 0) > 0 && (
                                <div className="mb-2" data-testid={`compliance-remediation-${evaluation.uid}`}>
                                    <span className="font-medium text-foreground">{t('asset.remediation_title')}</span>
                                    <ol className="mt-1 ml-4 list-decimal text-muted-foreground space-y-0.5">
                                        {rule!.remediation!.steps.map((step, i) => {
                                            // The diagnosis step is skipped when the (rule, asset) pair
                                            // already holds evidence issued within the gate window — a
                                            // recent survey that merely expired doesn't need redoing,
                                            // the remediation works do (spec 2026-07-17-01, lot 1).
                                            let skippedByEvidence: Date | null = null
                                            if (step.action === 'order_diagnosis' && step.skip_if_evidence_within_months != null) {
                                                const threshold = new Date()
                                                threshold.setMonth(threshold.getMonth() - step.skip_if_evidence_within_months)
                                                const recent = (evaluation.evidence ?? [])
                                                    .map(ev => (ev.issued_at ? new Date(ev.issued_at) : null))
                                                    .filter((d): d is Date => d != null && d >= threshold)
                                                    .sort((a, b) => b.getTime() - a.getTime())
                                                skippedByEvidence = recent[0] ?? null
                                            }
                                            return (
                                                <li key={i} className={skippedByEvidence ? 'line-through opacity-60' : ''} data-testid={skippedByEvidence ? `remediation-step-skipped-${i}` : undefined}>
                                                    {t(`asset.remediation_action_${step.action}`)}
                                                    {skippedByEvidence ? (
                                                        <span className="text-xs no-underline"> ({t('asset.remediation_step_skipped', { date: skippedByEvidence.toLocaleDateString() })})</span>
                                                    ) : (
                                                        <>
                                                            {step.action === 'order_diagnosis' && step.skip_if_evidence_within_months != null && (
                                                                <span className="text-xs"> ({t('asset.remediation_skip_hint', { months: step.skip_if_evidence_within_months })})</span>
                                                            )}
                                                        </>
                                                    )}
                                                    {step.action === 'file_claim' && (step.ladder?.length ?? 0) > 0 && (
                                                        <span className="text-xs"> ({step.ladder!.map(rung => t(`asset.remediation_ladder_${rung}`)).join(' → ')})</span>
                                                    )}
                                                </li>
                                            )
                                        })}
                                    </ol>
                                </div>
                            )}
                            {evaluation.details && (
                                <div>
                                    <span className="font-medium text-foreground">{t('asset.eval_details_label')}</span>
                                    <pre className="mt-1 text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap">
                                        {JSON.stringify(evaluation.details, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    )
}

/**
 * Shared validity-metadata fields (spec 2026-07-05-03 §6): issued_at,
 * valid_until (empty = permanent), note, page (mapped to source_details on
 * submit — never sent as a bare `page` field). Reused by both the attach
 * flow (below the document picker) and the edit dialog.
 */
function EvidenceValidityFields({ issuedAt, setIssuedAt, validUntil, setValidUntil, note, setNote, page, setPage, idPrefix }: {
    issuedAt: string
    setIssuedAt: (v: string) => void
    validUntil: string
    setValidUntil: (v: string) => void
    note: string
    setNote: (v: string) => void
    page: string
    setPage: (v: string) => void
    idPrefix: string
}) {
    const { t } = useTranslation('compliance')
    const dateOrderInvalid = !!issuedAt && !!validUntil && validUntil < issuedAt
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground" htmlFor={`${idPrefix}-issued-at`}>{t('asset.field_issued_at')}</label>
                    <input
                        id={`${idPrefix}-issued-at`}
                        type="date"
                        value={issuedAt}
                        onChange={(e) => setIssuedAt(e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid="evidence-issued-at"
                    />
                    <p className="text-[11px] text-muted-foreground">{t('asset.field_issued_at_hint')}</p>
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground" htmlFor={`${idPrefix}-valid-until`}>{t('asset.field_valid_until')}</label>
                    <input
                        id={`${idPrefix}-valid-until`}
                        type="date"
                        value={validUntil}
                        onChange={(e) => setValidUntil(e.target.value)}
                        className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${dateOrderInvalid ? 'border-destructive' : 'border-input'}`}
                        data-testid="evidence-valid-until"
                    />
                    <p className="text-[11px] text-muted-foreground">{t('asset.field_valid_until_hint')}</p>
                </div>
            </div>
            {dateOrderInvalid && (
                <p className="text-xs text-destructive" data-testid="evidence-date-order-error">{t('asset.validation_valid_until_before_issued')}</p>
            )}
            <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor={`${idPrefix}-note`}>{t('asset.field_note')}</label>
                <input
                    id={`${idPrefix}-note`}
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="evidence-note"
                />
                <p className="text-[11px] text-muted-foreground">{t('asset.field_note_hint')}</p>
            </div>
            <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor={`${idPrefix}-page`}>{t('asset.field_page')}</label>
                <input
                    id={`${idPrefix}-page`}
                    type="number"
                    min={1}
                    value={page}
                    onChange={(e) => setPage(e.target.value)}
                    className="w-32 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="evidence-page"
                />
                <p className="text-[11px] text-muted-foreground">{t('asset.field_page_hint')}</p>
            </div>
        </div>
    )
}

/** Builds the wire-shape validity payload from the form's plain string state. `page` becomes `source_details: {"page": n}` — never a bare `page` field (spec §6). */
function buildEvidencePayload(issuedAt: string, validUntil: string, note: string, page: string): EvidenceValidityInput {
    const pageNum = page.trim() === '' ? undefined : Number(page)
    return {
        issued_at: issuedAt || null,
        valid_until: validUntil || null,
        note: note || '',
        source_details: pageNum && !Number.isNaN(pageNum) ? { page: pageNum } : null,
    }
}

/**
 * Document picker scoped to an asset subtree, extended with the optional
 * validity fields (spec §6) so attaching a document and recording its
 * validity happen in one step. Selecting a document reveals the fields and
 * an explicit "Attach" confirm — it no longer links on click, since the user
 * may want to fill in issued_at/valid_until/note/page first. Documents
 * already linked to this rule are shown disabled. See spec 2026-06-20-04 §4.
 */
function EvidencePickerDialog({
    org,
    assetUid,
    open,
    onOpenChange,
    linkedDocumentUids,
    isLinking,
    onConfirm,
}: {
    org: string
    assetUid: string
    open: boolean
    onOpenChange: (open: boolean) => void
    linkedDocumentUids: Set<string>
    isLinking: boolean
    onConfirm: (documentUid: string, validity: EvidenceValidityInput) => void
}) {
    const { t } = useTranslation('compliance')
    const [search, setSearch] = useState('')
    const [selectedDocumentUid, setSelectedDocumentUid] = useState<string | null>(null)
    const [issuedAt, setIssuedAt] = useState('')
    const [validUntil, setValidUntil] = useState('')
    const [note, setNote] = useState('')
    const [page, setPage] = useState('')

    const { data: documents, isLoading } = useQuery({
        queryKey: ['asset-documents-for-evidence', org, assetUid],
        queryFn: () => apiFetch<PaginatedResponse<DocumentListItem>>(
            `/api/v1/organizations/${org}/documents?asset_uid=${assetUid}&limit=200`
        ),
        enabled: open && !!org && !!assetUid,
        select: (d) => d.data,
    })
    const resolver = useDocumentUrlResolver(org, search, open)

    const filtered = useMemo(() => {
        if (resolver.isPastedReference) return []
        const list = documents || []
        const q = search.trim().toLowerCase()
        if (!q) return list
        return list.filter((d) => d.name.toLowerCase().includes(q))
    }, [documents, search, resolver.isPastedReference])

    // When a pasted URL/UID resolves, show it as the sole pinned result —
    // same row shape as the normal list, so picking it works identically.
    const pastedResult: DocumentListItem[] = resolver.document ? [resolver.document] : []

    const dateOrderInvalid = !!issuedAt && !!validUntil && validUntil < issuedAt

    const renderDocRow = (doc: DocumentListItem) => {
        const alreadyLinked = linkedDocumentUids.has(doc.uid)
        const isSelected = selectedDocumentUid === doc.uid
        return (
            <button
                key={doc.uid}
                type="button"
                disabled={alreadyLinked || isLinking}
                onClick={() => setSelectedDocumentUid(doc.uid)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/50 disabled:opacity-50 disabled:cursor-not-allowed ${isSelected ? 'bg-accent' : ''}`}
                data-testid={`evidence-picker-option-${doc.uid}`}
            >
                {doc.thumbnailUrl ? (
                    <img src={doc.thumbnailUrl} alt="" className="h-7 w-7 flex-shrink-0 rounded object-cover" onError={(e) => { e.currentTarget.style.display = 'none' }} />
                ) : (
                    <AppIcon tone="muted" path={SECTION_ICONS.documents} size="sm" className="flex-shrink-0" />
                )}
                <span className="truncate flex-1">{doc.name}</span>
                {alreadyLinked && (
                    <span className="text-xs text-muted-foreground">{t('asset.already_linked')}</span>
                )}
            </button>
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="evidence-picker-dialog">
                <DialogHeader>
                    <DialogTitle>{t('asset.pick_document_title')}</DialogTitle>
                    <DialogDescription>{t('asset.pick_document_description')}</DialogDescription>
                </DialogHeader>
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('asset.pick_document_search')}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="evidence-picker-search"
                    autoFocus
                />
                <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border">
                    {resolver.isPastedReference ? (
                        resolver.isLoading ? (
                            <div className="p-4 text-sm text-muted-foreground text-center">{t('asset.pick_document_loading')}</div>
                        ) : pastedResult.length === 0 ? (
                            <EmptyState
                                variant="inline"
                                live
                                title={t('asset.pick_document_empty')}
                                data-testid="evidence-picker-empty"
                            />
                        ) : (
                            pastedResult.map((doc) => renderDocRow(doc))
                        )
                    ) : isLoading ? (
                        <div className="p-4 text-sm text-muted-foreground text-center">{t('asset.pick_document_loading')}</div>
                    ) : filtered.length === 0 ? (
                        <EmptyState
                            variant="inline"
                            live
                            title={t('asset.pick_document_empty')}
                            data-testid="evidence-picker-empty"
                        />
                    ) : (
                        filtered.map((doc) => renderDocRow(doc))
                    )}
                </div>
                {selectedDocumentUid && (
                    <EvidenceValidityFields
                        issuedAt={issuedAt} setIssuedAt={setIssuedAt}
                        validUntil={validUntil} setValidUntil={setValidUntil}
                        note={note} setNote={setNote}
                        page={page} setPage={setPage}
                        idPrefix="evidence-attach"
                    />
                )}
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="evidence-picker-cancel">
                        {t('asset.pick_document_cancel')}
                    </Button>
                    {selectedDocumentUid && (
                        <Button
                            size="sm"
                            disabled={isLinking || dateOrderInvalid}
                            onClick={() => onConfirm(selectedDocumentUid, buildEvidencePayload(issuedAt, validUntil, note, page))}
                            data-testid="evidence-attach-confirm"
                        >
                            {isLinking ? t('asset.evidence_saving') : t('asset.attach_evidence')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

/**
 * Edit an existing evidence link's validity metadata (spec §6). The linked
 * document itself is immutable here — detach and re-attach to change it, so
 * this dialog only ever shows the four validity fields, pre-filled from the
 * target evidence item, and PATCHes them on save.
 */
function EvidenceEditDialog({ org, assetUid, ruleUid, evidence, open, onOpenChange, onSaved }: {
    org: string
    assetUid: string
    ruleUid: string
    evidence: ComplianceEvidenceItem
    open: boolean
    onOpenChange: (open: boolean) => void
    onSaved: () => void
}) {
    const { t } = useTranslation('compliance')
    const patchEvidence = usePatchEvidence(org, assetUid)
    const [issuedAt, setIssuedAt] = useState(evidence.issued_at ? evidence.issued_at.slice(0, 10) : '')
    const [validUntil, setValidUntil] = useState(evidence.valid_until ? evidence.valid_until.slice(0, 10) : '')
    const [note, setNote] = useState(evidence.note || '')
    const [page, setPage] = useState(() => {
        const sourcePage = (evidence.source_details as { page?: number } | null)?.page
        return typeof sourcePage === 'number' ? String(sourcePage) : ''
    })

    const dateOrderInvalid = !!issuedAt && !!validUntil && validUntil < issuedAt

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="evidence-edit-dialog">
                <DialogHeader>
                    <DialogTitle>{t('asset.edit_evidence_title')}</DialogTitle>
                    <DialogDescription>{t('asset.edit_evidence_description')} — {evidence.document_name}</DialogDescription>
                </DialogHeader>
                <EvidenceValidityFields
                    issuedAt={issuedAt} setIssuedAt={setIssuedAt}
                    validUntil={validUntil} setValidUntil={setValidUntil}
                    note={note} setNote={setNote}
                    page={page} setPage={setPage}
                    idPrefix="evidence-edit"
                />
                {patchEvidence.isError && (
                    <p className="text-xs text-destructive">{t('asset.evidence_save_error')}</p>
                )}
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="evidence-edit-cancel">
                        {t('asset.evidence_cancel')}
                    </Button>
                    <Button
                        size="sm"
                        disabled={patchEvidence.isPending || dateOrderInvalid}
                        onClick={() => {
                            const validity: EvidenceValidityInput = buildEvidencePayload(issuedAt, validUntil, note, page)
                            patchEvidence.mutate(
                                { ruleUid, evidenceUid: evidence.uid, ...validity },
                                { onSuccess: onSaved }
                            )
                        }}
                        data-testid="evidence-edit-save"
                    >
                        {patchEvidence.isPending ? t('asset.evidence_saving') : t('asset.evidence_save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
